import React, { useState } from 'react';
import {
  View,
  Text,
  TextInput,
  Pressable,
  StyleSheet,
  KeyboardAvoidingView,
  Platform,
  StatusBar,
  ActivityIndicator,
  Dimensions,
  ScrollView,
} from 'react-native';
import { useAuth } from '../services/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import CheckerPattern from '../components/CheckerPattern';

const { width } = Dimensions.get('window');

const LinkAccountScreen = ({ navigation, route }) => {
  const { email, provider } = route?.params || {};
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  
  const { linkProviderToAccount, signInWithGoogle, signInWithApple } = useAuth();
  const { isDarkMode } = useTheme();

  // Theme colors
  const theme = {
    bg: isDarkMode ? '#0A0A0A' : '#FAFAFA',
    card: isDarkMode ? '#141414' : '#FFFFFF',
    text: isDarkMode ? '#FFFFFF' : '#0A0A0A',
    textSecondary: isDarkMode ? '#888888' : '#666666',
    border: isDarkMode ? '#2A2A2A' : '#E5E5E5',
    inputBg: isDarkMode ? '#1A1A1A' : '#F5F5F5',
    accent: '#94EB98',
    accentDark: '#7BC97F',
    error: '#FF6B6B',
  };

  const providerName = provider === 'Google' ? 'Google' : 'Apple';

  const handleLinkAccount = async () => {
    if (!password) {
      setErrorMessage('Please enter your password');
      return;
    }

    setErrorMessage('');
    setLoading(true);

    try {
      const result = await linkProviderToAccount(email, password, provider);
      setLoading(false);

      if (result.success) {
        // After linking, re-initiate social sign-in
        // This will now link to the verified account
        if (provider === 'Google') {
          await signInWithGoogle();
        } else {
          await signInWithApple();
        }
      } else {
        setErrorMessage(result.error || 'Failed to link account. Please try again.');
      }
    } catch (error) {
      setLoading(false);
      setErrorMessage(error.message || 'An error occurred');
    }
  };

  const handleCancel = () => {
    navigation.goBack();
  };

  return (
    <View style={[styles.container, { backgroundColor: theme.bg }]}>
      <StatusBar barStyle={isDarkMode ? 'light-content' : 'dark-content'} />
      
      {/* Background Pattern */}
      <View style={styles.patternContainer}>
        <CheckerPattern />
      </View>

      <KeyboardAvoidingView
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        style={styles.keyboardView}
      >
        <ScrollView
          contentContainerStyle={styles.scrollContent}
          keyboardShouldPersistTaps="handled"
          showsVerticalScrollIndicator={false}
        >
          <View style={[styles.card, { backgroundColor: theme.card }]}>
            {/* Header */}
            <View style={styles.header}>
              <Text style={[styles.title, { color: theme.text }]}>
                Link Your Account
              </Text>
              <Text style={[styles.subtitle, { color: theme.textSecondary }]}>
                We found an existing account with the email{' '}
                <Text style={{ fontWeight: '600', color: theme.text }}>{email}</Text>
              </Text>
              <Text style={[styles.description, { color: theme.textSecondary }]}>
                Enter your password to link your {providerName} account.
                After linking, you can use either method to sign in.
              </Text>
            </View>

            {/* Error Message */}
            {errorMessage ? (
              <View style={[styles.errorContainer, { backgroundColor: theme.error + '15' }]}>
                <Text style={[styles.errorText, { color: theme.error }]}>
                  {errorMessage}
                </Text>
              </View>
            ) : null}

            {/* Password Input */}
            <View style={styles.inputContainer}>
              <Text style={[styles.inputLabel, { color: theme.textSecondary }]}>
                Password
              </Text>
              <TextInput
                style={[
                  styles.input,
                  { color: theme.text, borderBottomColor: theme.border },
                ]}
                placeholder="Enter your password"
                placeholderTextColor={theme.textSecondary}
                value={password}
                onChangeText={setPassword}
                secureTextEntry
                autoCapitalize="none"
                autoCorrect={false}
              />
            </View>

            {/* Link Button */}
            <Pressable
              style={({ pressed }) => [
                styles.linkButton,
                { backgroundColor: theme.accent },
                pressed && styles.buttonPressed,
                loading && styles.buttonDisabled,
              ]}
              onPress={handleLinkAccount}
              disabled={loading}
            >
              {loading ? (
                <ActivityIndicator color="#000" />
              ) : (
                <Text style={styles.linkButtonText}>
                  Link {providerName} Account
                </Text>
              )}
            </Pressable>

            {/* Cancel Button */}
            <Pressable
              style={({ pressed }) => [
                styles.cancelButton,
                { borderColor: theme.border },
                pressed && styles.buttonPressed,
              ]}
              onPress={handleCancel}
              disabled={loading}
            >
              <Text style={[styles.cancelButtonText, { color: theme.text }]}>
                Cancel
              </Text>
            </Pressable>

            {/* Help Text */}
            <Text style={[styles.helpText, { color: theme.textSecondary }]}>
              Forgot your password?{' '}
              <Text
                style={{ color: theme.accent }}
                onPress={() => navigation.navigate('ForgotPassword', { email })}
              >
                Reset it here
              </Text>
            </Text>
          </View>
        </ScrollView>
      </KeyboardAvoidingView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  patternContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    opacity: 0.03,
  },
  keyboardView: {
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
    justifyContent: 'center',
    padding: 20,
  },
  card: {
    borderRadius: 24,
    padding: 32,
    maxWidth: 400,
    width: '100%',
    alignSelf: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 12,
    elevation: 5,
  },
  header: {
    marginBottom: 24,
  },
  title: {
    fontSize: 28,
    fontWeight: '700',
    marginBottom: 12,
    textAlign: 'center',
  },
  subtitle: {
    fontSize: 15,
    lineHeight: 22,
    textAlign: 'center',
    marginBottom: 8,
  },
  description: {
    fontSize: 14,
    lineHeight: 20,
    textAlign: 'center',
  },
  errorContainer: {
    padding: 12,
    borderRadius: 12,
    marginBottom: 16,
  },
  errorText: {
    fontSize: 14,
    textAlign: 'center',
  },
  inputContainer: {
    marginBottom: 20,
  },
  inputLabel: {
    fontSize: 13,
    fontWeight: '600',
    marginBottom: 8,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  input: {
    height: 52,
    borderRadius: 12,
    paddingHorizontal: 16,
    fontSize: 16,
    borderWidth: 0,
    borderBottomWidth: 1,
    backgroundColor: 'transparent',
    ...(Platform.OS === 'web' && { outline: 'none' }),
  },
  linkButton: {
    height: 52,
    borderRadius: 12,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 12,
  },
  linkButtonText: {
    color: '#000',
    fontSize: 16,
    fontWeight: '600',
  },
  cancelButton: {
    height: 52,
    borderRadius: 12,
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 1,
    marginBottom: 16,
  },
  cancelButtonText: {
    fontSize: 16,
    fontWeight: '600',
  },
  buttonPressed: {
    opacity: 0.8,
    transform: [{ scale: 0.98 }],
  },
  buttonDisabled: {
    opacity: 0.6,
  },
  helpText: {
    fontSize: 14,
    textAlign: 'center',
  },
});

export default LinkAccountScreen;
